import { vec2 } from "@tlaukkan/tsm";
import { Entity, System } from "ecsy";
import { Cooldown, Infected, LaneInfo, Renderable, Spawner, Transform, Virus } from "../../../shared/components";
import { getEntityPlayer } from "../../../shared/composition";
import { Camera } from "../../../shared/constants";
import { getEntityById } from "../../../shared/entityFactory";
import { Player } from "../../../shared/player";
import { GameClient } from "../gameClient";

export enum PointOfInterestType {
	Central,  // camera pans to fit these
	Fringe,   // camera zooms out to fit these, no pan
}

export class PointOfInterest {
	constructor(
		public type: PointOfInterestType,
		public position: vec2,
		public radius: number,
	) {
	}
}

export class PointOfInterestSystem extends System {
	private game: GameClient;

	centralPOIs: PointOfInterest[] = [];
	fringePOIs: PointOfInterest[] = [];

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute() {
		this.centralPOIs = [];
		this.fringePOIs = [];

		if (!this.game.inSimulation) return;

		const player = this.game.getPlayer();
		if (!player) return;

		this.updatePointsOfInterest(player);
	}

	updatePointsOfInterest(player: Player) {
		// Build active lanes per targeted player and add all viruses as Fringe
		const activeLanesByPlayer = new Map<number, Set<number>>();
		for (const entity of this.queries.virus.results) {
			const laneInfo = entity.getComponent(LaneInfo);
			const owner = getEntityPlayer(entity, this.game);
			if (laneInfo && laneInfo.lane >= 0 && owner) {
				if (!activeLanesByPlayer.has(owner.id)) {
					activeLanesByPlayer.set(owner.id, new Set());
				}
				activeLanesByPlayer.get(owner.id)!.add(laneInfo.lane);
			}
			const transform = entity.getComponent(Transform);
			this.addPointOfInterest(PointOfInterestType.Fringe, transform.position, transform.size.x);
		}

		// Find infected cells of interest, give them Central, track which players' blood cells to add
		const playerIdsForBloodCells = new Set<number>();
		let hasAnyCentralInterest = false;

		for (const entity of this.queries.infected.results) {
			const laneInfo = entity.getComponent(LaneInfo);
			const spawner = entity.getComponent(Spawner);
			const cooldown = entity.getComponent(Cooldown);
			const infected = entity.getComponent(Infected);

			const hasActiveVirus = laneInfo != null && infected.targetPlayerIds.some(
				id => activeLanesByPlayer.get(id)?.has(laneInfo!.lane)
			);
			const isAboutToSpawn = spawner.amount > 0 && cooldown.getTimeLeft() < Camera.InfectedSpawnAutoFocusNoticeSeconds;

			if (!hasActiveVirus && !isAboutToSpawn) continue;

			const transform = entity.getComponent(Transform);
			this.addPointOfInterest(PointOfInterestType.Central, transform.position, 1);
			hasAnyCentralInterest = true;

			// Record which players are targeted so we can add their blood cells
			for (const targetId of infected.targetPlayerIds) {
				if (targetId >= 0) playerIdsForBloodCells.add(targetId);
			}
		}

		// Add blood cells of all targeted players as Central
		if (hasAnyCentralInterest) {
			for (const p of this.game.players) {
				if (!playerIdsForBloodCells.has(p.id)) continue;
				const bloodcells = p.hpCells
					.map(cell => getEntityById(cell.id))
					.filter(cell => cell !== null) as Entity[];
				for (const entity of bloodcells) {
					const transform = entity.getComponent(Transform);
					this.addPointOfInterest(PointOfInterestType.Central, transform.position, transform.size.x);
				}
			}
		}
	}

	addPointOfInterest(type: PointOfInterestType, position: vec2, radius: number) {
		const poi = new PointOfInterest(type, position, radius);
		if (type === PointOfInterestType.Central) {
			this.centralPOIs.push(poi);
		} else {
			this.fringePOIs.push(poi);
		}
	}

	calculateRectangleOfInterest(): { center: vec2, width: number, height: number } | null {
		if (this.centralPOIs.length === 0) return null;

		let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
		for (const poi of this.centralPOIs) {
			minX = Math.min(minX, poi.position.x - poi.radius);
			minY = Math.min(minY, poi.position.y - poi.radius);
			maxX = Math.max(maxX, poi.position.x + poi.radius);
			maxY = Math.max(maxY, poi.position.y + poi.radius);
		}
		const centerX = (minX + maxX) / 2;
		const centerY = (minY + maxY) / 2;

		// Expand from the Central center to cover Fringe POIs (zoom only, no pan)
		let halfW = (maxX - minX) / 2;
		let halfH = (maxY - minY) / 2;
		for (const poi of this.fringePOIs) {
			halfW = Math.max(halfW, Math.abs(poi.position.x - centerX) + poi.radius);
			halfH = Math.max(halfH, Math.abs(poi.position.y - centerY) + poi.radius);
		}

		return {
			center: new vec2([centerX, centerY]),
			width: halfW * 2,
			height: halfH * 2,
		};
	}

	entityIsOnSharedLane(entity: Entity, player: Player) {
		const laneInfo = entity.getComponent(LaneInfo);
		if (!laneInfo) return false;
		return player.sharedLanes.some(lane => lane.lane === laneInfo.lane);
	}
}

PointOfInterestSystem.queries = {
	virus: {
		components: [Renderable, Transform, Virus],
	},
	infected: {
		components: [Infected, Spawner, Transform],
	}
}
